import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useConvexAuth, useAction } from 'convex/react';
import { useAuthActions, useAuthToken } from '@convex-dev/auth/react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../convex/_generated/api';
import useAppStore from './stores/appStore';
import useFileStore from './stores/fileStore';
import useAIStore from './stores/aiStore';
import usePendingChangesStore from './stores/pendingChangesStore';
import { loadAndChangeLanguage } from './i18n';
import i18n from './i18n';
import AuthGate from './components/Auth/AuthGate';
import OnboardingWizard from './components/Onboarding/OnboardingWizard';
import ProjectHub from './components/Projects/ProjectHub';
import ProjectHome from './components/Projects/ProjectHome';
import DraftReviewPage from './components/Projects/DraftReviewPage';
import AudiobookStudioPage from './components/Projects/AudiobookStudioPage';
import AccessibleStudioPage from './components/Projects/AccessibleStudioPage';
import TranslationStudioPage from './components/Projects/TranslationStudioPage';
import RewardsPage from './components/Rewards/RewardsPage';
import MarketingStudioPage from './components/Projects/MarketingStudioPage';
import StorefrontStudioPage from './components/Projects/StorefrontStudioPage';
import StoryBiblePage from './components/Projects/StoryBiblePage';
import ReaderPreviewPage from './components/Projects/ReaderPreviewPage';
import AudienceTestPage from './components/Projects/AudienceTestPage';
import CanvasStudioPage from './components/Projects/CanvasStudioPage';
import PublicStorefrontPage from './components/Storefront/PublicStorefrontPage';
import ChallengeAdminPage from './components/Challenges/ChallengeAdminPage';
import ChallengeWorkspacePage from './components/Challenges/ChallengeWorkspacePage';
import ChallengeSubmissionPage from './components/Challenges/ChallengeSubmissionPage';
import ChallengeWritePage from './components/Challenges/ChallengeWritePage';
import CommunityPage from './components/Community/CommunityPage';
import ProfilePage from './components/Community/ProfilePage';
import PublicCommunityProfile from './components/Community/PublicCommunityProfile';
import PublicCommunityPost from './components/Community/PublicCommunityPost';
import { isValidProjectId, normalizeProjectId } from './utils/projectIds';
import { CREDIT_PAYMENT_URL } from '../shared/billing';

import AppLayout from './components/Layout/AppLayout';
import './components/App.css';

class AppErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error('App render error:', error, info);
    }

    componentDidUpdate(prevProps) {
        // Reset error state when children change (e.g. navigation)
        if (this.state.hasError && prevProps.children !== this.props.children) {
            this.setState({ hasError: false, error: null });
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="app-loading">
                    <div className="loading-content">
                        <div className="loading-logo">✒️</div>
                        <div className="loading-text">{i18n.t('common.somethingWentWrong')}</div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                            <button className="btn btn-primary" onClick={() => window.location.reload()}>
                                {i18n.t('common.reload')}
                            </button>
                            <button className="btn btn-secondary" onClick={() => { window.location.href = '/projects'; }}>
                                {i18n.t('common.backToProjects')}
                            </button>
                        </div>
                        {this.state.error && (
                            <div style={{ marginTop: '16px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', maxWidth: '400px', wordBreak: 'break-word', textAlign: 'center' }}>
                                {this.state.error.message || String(this.state.error)}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

function App() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setCurrentProjectId, setHasCompletedOnboarding, checkDailyReset, reset: resetApp } =
        useAppStore();
    const { loadFiles, reset: resetFiles } = useFileStore();
    const resetAI = useAIStore((state) => state.reset);
    const clearPending = usePendingChangesStore((state) => state.clear);
    const [isCreatingProject, setIsCreatingProject] = useState(false);
    const authToken = useAuthToken();
    const authReady = authToken != null && authToken !== undefined;
    const { signOut } = useAuthActions();
    const { isAuthenticated: isConvexAuthed, isLoading: isConvexLoading } = useConvexAuth();
    const [authStalled, setAuthStalled] = useState(false);
    const createProjectMutation = useMutation(api.projects.create);
    const updateFileContent = useMutation(api.files.updateContent);
    const generateOutline = useAction(api.ai.generateOutline);
    const projects = useQuery(api.projects.listWithStats, (authReady && isConvexAuthed) ? {} : "skip");
    const currentUser = useQuery(api.users.getCurrentUser, (authReady && isConvexAuthed) ? {} : "skip");

    useEffect(() => {
        if (currentUser?.profile?.language) {
            const serverLang = currentUser.profile.language;
            const { language: localLang, setLanguage } = useAppStore.getState();
            if (serverLang !== localLang) {
                setLanguage(serverLang);
                loadAndChangeLanguage(serverLang);
            }
        }
    }, [currentUser?.profile?.language]);

    const openProject = (rawProjectId) => {
        const projectId = normalizeProjectId(rawProjectId);
        if (!isValidProjectId(projectId)) {
            console.error('Invalid project ID:', rawProjectId);
            return;
        }
        navigate(`/projects/${projectId}`);
    };

    useEffect(() => {
        if (authReady && !isConvexAuthed) {
            const timeout = setTimeout(() => setAuthStalled(true), 8000);
            return () => clearTimeout(timeout);
        }
        setAuthStalled(false);
        return undefined;
    }, [authReady, isConvexAuthed]);

    useEffect(() => {
        if (!authReady) {
            setCurrentProjectId(null);
        }
    }, [authReady, setCurrentProjectId]);



    // Handle onboarding completion
    const handleOnboardingComplete = async (onboardingData) => {
        setIsCreatingProject(true);

        try {
            // Create the project
            const projectId = await createProjectMutation({
                title: onboardingData.title,
                synopsis: onboardingData.synopsis,
                genre: onboardingData.genre,
                targetAudience: onboardingData.targetAudience,
                mentor: onboardingData.mentor,
                experienceLevel: onboardingData.experienceLevel
            });

            // Generate outline content using AI
            try {
                const outlineResponse = await generateOutline({
                    projectId,
                    title: onboardingData.title,
                    synopsis: onboardingData.synopsis,
                    genre: onboardingData.genre,
                    targetAudience: onboardingData.targetAudience,
                    experienceLevel: onboardingData.experienceLevel,
                    setting: onboardingData.setting,
                    characters: onboardingData.characters,
                    tone: onboardingData.tone
                });

                if (outlineResponse?.content) {
                    await updateFileContent({
                        projectId,
                        path: 'outline.md',
                        content: outlineResponse.content
                    });
                }
            } catch (e) {
                console.error('AI generation failed, using fallback:', e);
            }

            setHasCompletedOnboarding(true);
            navigate(`/projects/${projectId}`);

        } catch (error) {
            console.error('Failed to create project:', error);
        } finally {
            setIsCreatingProject(false);
        }
    };

    const isAuthLoading =
        isConvexLoading || (authReady && !isConvexAuthed && !authStalled);

    const hasAuthCode =
        typeof window !== 'undefined' &&
        (new URLSearchParams(window.location.search).has('code') ||
            new URLSearchParams(window.location.search).has('error'));
    let protectedFallback = null;
    if (isAuthLoading) {
        protectedFallback = (
            <div className="app-loading">
                <div className="loading-content">
                    <div className="loading-logo">✒️</div>
                    <div className="loading-text">{t('auth.loadingSharePen')}</div>
                </div>
            </div>
        );
    } else if (hasAuthCode) {
        protectedFallback = (
            <div className="app-loading">
                <div className="loading-content">
                    <div className="loading-logo">✒️</div>
                    <div className="loading-text">{t('auth.completingSignIn')}</div>
                </div>
            </div>
        );
    } else if (!authReady) {
        protectedFallback = <AuthGate />;
    } else if (authReady && !isConvexAuthed && authStalled) {
        protectedFallback = (
            <AuthStallScreen
                onReset={async () => {
                    await signOut();
                    resetApp();
                    resetFiles();
                    resetAI();
                    clearPending();
                    window.location.href = "/";
                }}
            />
        );
    } else if (!isConvexAuthed) {
        protectedFallback = (
            <div className="app-loading">
                <div className="loading-content">
                    <div className="loading-logo">✒️</div>
                    <div className="loading-text">{t('auth.finalizingSession')}</div>
                </div>
            </div>
        );
    } else if (projects === undefined) {
        protectedFallback = (
            <div className="app-loading">
                <div className="loading-content">
                    <div className="loading-logo">✒️</div>
                    <div className="loading-text">{t('auth.syncingProjects')}</div>
                </div>
            </div>
        );
    }

    return (
        <AppErrorBoundary>
            <Routes>
                <Route path="/community/:handle" element={<PublicCommunityProfile />} />
                <Route path="/community/post/:postId" element={<PublicCommunityPost />} />
                <Route path="/community" element={<Navigate to="/projects/community" replace />} />
                <Route path="/billing" element={<BillingRedirect />} />
                {protectedFallback ? (
                    <>
                        <Route path="/projects/:projectId/:section" element={protectedFallback} />
                        <Route path="/challenges/:slug/:section" element={protectedFallback} />
                    </>
                ) : null}
                <Route path="/:username/:projectSlug/:bookSlug" element={<PublicStorefrontPage />} />
                {protectedFallback ? (
                    <Route path="*" element={protectedFallback} />
                ) : (
                    <>
                        <Route path="/" element={<Navigate to="/projects" replace />} />
                        <Route
                            path="/projects"
                            element={
                                <ProjectHub
                                    projects={projects}
                                    onOpenProject={openProject}
                                    onCreateProject={() => navigate('/onboarding')}
                                    onOpenCommunity={() => navigate('/projects/community')}
                                    onOpenChallenges={() => navigate('/projects/challenges')}
                                    onOpenChallengeAdmin={() => navigate('/projects/challenges-admin')}
                                    onResetWorkspace={() => resetFiles()}
                                />
                            }
                        />
                        <Route path="/projects/community" element={<CommunityPage mode="discover" />} />
                        <Route path="/projects/community/following" element={<CommunityPage mode="following" />} />
                        <Route path="/projects/community/soundbites" element={<CommunityPage mode="soundbites" />} />
                        <Route path="/projects/community/saved" element={<CommunityPage mode="saved" />} />
                        <Route path="/projects/community/notifications" element={<CommunityPage mode="notifications" />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/projects/:projectId" element={<ProjectHome />} />
                        <Route path="/projects/:projectId/draft-review" element={<DraftReviewPage />} />
                        <Route path="/projects/:projectId/accessible" element={<AccessibleStudioPage />} />
                        <Route path="/projects/:projectId/audiobook" element={<AudiobookStudioPage />} />
                        <Route path="/projects/:projectId/translation" element={<TranslationStudioPage />} />
                        <Route path="/projects/rewards" element={<RewardsPage />} />
                        <Route path="/projects/:projectId/reader-preview" element={<ReaderPreviewPage />} />
                        <Route path="/projects/:projectId/reader-preview/*" element={<ReaderPreviewPage />} />
                        <Route path="/projects/:projectId/readers-preview" element={<ReaderPreviewAlias />} />
                        <Route path="/projects/:projectId/marketing" element={<MarketingStudioPage />} />
                        <Route path="/projects/:projectId/storefront" element={<StorefrontStudioPage />} />
                        <Route path="/projects/:projectId/audience-test" element={<AudienceTestPage />} />
                        <Route path="/projects/:projectId/canvas" element={<CanvasStudioPage />} />
                        <Route path="/projects/:projectId/story-bible" element={<StoryBiblePage />} />
                        <Route path="/projects/challenges" element={<ChallengeWorkspacePage />} />
                        <Route path="/projects/challenges-admin" element={<ChallengeAdminPage />} />
                        <Route path="/challenges/:slug/write" element={<ChallengeWritePage />} />
                        <Route path="/challenges/:slug/submission" element={<ChallengeSubmissionPage />} />
                        <Route
                            path="/onboarding"
                            element={
                                <OnboardingWizard
                                    onComplete={handleOnboardingComplete}
                                    onCancel={() => navigate('/projects')}
                                    isCreating={isCreatingProject}
                                />
                            }
                        />
                        <Route path="/projects/:projectId/editor" element={<ProjectWorkspace />} />
                        <Route path="*" element={<Navigate to="/projects" replace />} />
                    </>
                )}
            </Routes>
        </AppErrorBoundary>
    );
}

function ReaderPreviewAlias() {
    const { projectId } = useParams();
    const normalized = normalizeProjectId(projectId);
    if (!isValidProjectId(normalized)) {
        return <Navigate to="/projects" replace />;
    }
    return <Navigate to={`/projects/${normalized}/reader-preview`} replace />;
}

function BillingRedirect() {
    const { t } = useTranslation();

    useEffect(() => {
        window.location.replace(CREDIT_PAYMENT_URL);
    }, []);

    return (
        <div className="app-loading">
            <div className="loading-content">
                <div className="loading-logo">✒️</div>
                <div className="loading-text">
                    {t('billing.openingSecureCheckout', { defaultValue: 'Opening secure checkout...' })}
                </div>
            </div>
        </div>
    );
}

function AuthStallScreen({ onReset }) {
    const { t } = useTranslation();
    return (
        <div className="app-loading">
            <div className="loading-content">
                <div className="loading-logo">✒️</div>
                <div className="loading-text">
                    {t('auth.authStallMessage')}
                </div>
                <div className="loading-actions">
                    <button className="btn btn-secondary" onClick={() => window.location.reload()}>
                        {t('common.reload')}
                    </button>
                    <button className="btn btn-primary" onClick={onReset}>
                        {t('auth.signOutAndRetry')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProjectWorkspace() {
    const { t } = useTranslation();
    const { projectId } = useParams();
    const safeProjectId = isValidProjectId(projectId) ? projectId : null;
    const { setCurrentProjectId, checkDailyReset } = useAppStore();
    const { loadFiles } = useFileStore();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        if (!safeProjectId) {
            return undefined;
        }

        const loadProject = async () => {
            setIsLoading(true);
            setCurrentProjectId(safeProjectId);
            checkDailyReset();
            await loadFiles(safeProjectId);
            if (!cancelled) {
                setIsLoading(false);
            }
        };

        loadProject();

        return () => {
            cancelled = true;
        };
    }, [safeProjectId, setCurrentProjectId, checkDailyReset, loadFiles]);

    if (!safeProjectId) {
        return (
            <div className="app-loading">
                <div className="loading-content">
                    <div className="loading-logo">✒️</div>
                    <div className="loading-text">{t('common.invalidProjectLink')}</div>
                    <button className="btn btn-primary" onClick={() => window.location.assign('/projects')}>
                        {t('common.backToProjects')}
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="app-loading">
                <div className="loading-content">
                    <div className="loading-logo">✒️</div>
                    <div className="loading-text">{t('projects.loadingWorkspace')}</div>
                </div>
            </div>
        );
    }

    return <AppLayout projectId={safeProjectId} />;
}

export default App;
